import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Spinner } from '@openedx/paragon';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';

import { getSurvey, parseSurveyJson, submitSurveyAnswer } from './data/api';
import messages from './messages';

const RATING_VALUES = [1, 2, 3, 4, 5];
const SCORING_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function hasAnswer(value) {
  return value !== undefined && value !== null && value !== '';
}

const SurveyForm = ({
  courseSurvey,
  intl,
  onCancel,
  onSubmitted,
}) => {
  const [survey, setSurvey] = useState(null);
  const [answersByQuestionId, setAnswersByQuestionId] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadSurvey() {
      setLoading(true);
      setLoadError(false);
      try {
        const data = await getSurvey(courseSurvey.survey);
        if (isMounted) {
          setSurvey(data);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadSurvey();

    return () => {
      isMounted = false;
    };
  }, [courseSurvey.survey]);

  const questions = useMemo(
    () => parseSurveyJson(survey?.definition, []),
    [survey],
  );

  const updateAnswer = (questionId, value) => {
    setAnswersByQuestionId(currentAnswers => ({
      ...currentAnswers,
      [questionId]: value,
    }));
  };

  const isQuestionMissingAnswer = question => question.required && !hasAnswer(answersByQuestionId[question.id]);

  const buildAnswersPayload = () => questions
    .filter(question => hasAnswer(answersByQuestionId[question.id]))
    .map(question => ({
      id: question.id,
      question: question.question,
      answer: answersByQuestionId[question.id],
    }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitAttempted(true);
    setSubmitError(false);

    if (questions.some(isQuestionMissingAnswer)) {
      return;
    }

    setSubmitting(true);
    try {
      await submitSurveyAnswer(courseSurvey.id, buildAnswersPayload());
      onSubmitted();
    } catch (error) {
      setSubmitError(true);
      setSubmitting(false);
    }
  };

  const renderHelpText = (question) => {
    const lowHighText = [question.low, question.high].filter(Boolean).join(' - ');
    const helpText = [lowHighText, question.help].filter(Boolean).join(': ');

    if (!helpText) {
      return null;
    }

    return <div className="small text-gray-700 mt-2">{helpText}</div>;
  };

  const renderValidation = (question) => {
    if (!submitAttempted || !isQuestionMissingAnswer(question)) {
      return null;
    }

    return (
      <div className="invalid-feedback d-block">
        {intl.formatMessage(messages.requiredQuestion)}
      </div>
    );
  };

  const renderChoiceButtons = (question, values, className) => {
    const selectedAnswer = answersByQuestionId[question.id];

    return (
      <div
        role="radiogroup"
        aria-labelledby={`survey-question-${question.id}`}
        className={className}
      >
        {values.map(value => (
          <Button
            key={value}
            type="button"
            variant={selectedAnswer === value ? 'primary' : 'outline-primary'}
            className="survey-choice-button"
            onClick={() => updateAnswer(question.id, value)}
            aria-pressed={selectedAnswer === value}
          >
            {value}
          </Button>
        ))}
      </div>
    );
  };

  const renderQuestionInput = (question) => {
    const value = answersByQuestionId[question.id] || '';
    const isInvalid = submitAttempted && isQuestionMissingAnswer(question);

    if (question.type === 'rating') {
      return (
        <>
          {renderChoiceButtons(question, RATING_VALUES, 'survey-rating-group')}
          {renderHelpText(question)}
          {renderValidation(question)}
        </>
      );
    }

    if (question.type === 'scoring_10') {
      return (
        <>
          {renderChoiceButtons(question, SCORING_VALUES, 'survey-scoring-group')}
          {renderHelpText(question)}
          {renderValidation(question)}
        </>
      );
    }

    if (question.type === 'textarea') {
      return (
        <>
          <textarea
            id={`survey-answer-${question.id}`}
            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
            rows="4"
            value={value}
            onChange={event => updateAnswer(question.id, event.target.value)}
            aria-invalid={isInvalid}
          />
          {renderHelpText(question)}
          {renderValidation(question)}
        </>
      );
    }

    if (question.type === 'select') {
      return (
        <>
          <select
            id={`survey-answer-${question.id}`}
            className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
            value={value}
            onChange={event => updateAnswer(question.id, event.target.value)}
            aria-invalid={isInvalid}
          >
            <option value="">{intl.formatMessage(messages.chooseOption)}</option>
            {(question.values || []).map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {renderHelpText(question)}
          {renderValidation(question)}
        </>
      );
    }

    return (
      <>
        <input
          id={`survey-answer-${question.id}`}
          className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
          type="text"
          value={value}
          onChange={event => updateAnswer(question.id, event.target.value)}
          aria-invalid={isInvalid}
        />
        {renderHelpText(question)}
        {renderValidation(question)}
      </>
    );
  };

  if (loading) {
    return (
      <div className="survey-loading">
        <Spinner
          animation="border"
          variant="primary"
          screenReaderText={intl.formatMessage(messages.loadingSurvey)}
        />
      </div>
    );
  }

  if (loadError) {
    return <Alert variant="danger">{intl.formatMessage(messages.loadError)}</Alert>;
  }

  if (!Array.isArray(questions)) {
    return <Alert variant="danger">{intl.formatMessage(messages.invalidDefinition)}</Alert>;
  }

  return (
    <form className="survey-form" onSubmit={handleSubmit}>
      <div className="survey-form-header">
        <h2 className="h3">{survey?.name || courseSurvey.survey_name}</h2>
        {survey?.description && <p className="text-gray-700 mb-0">{survey.description}</p>}
      </div>

      {submitError && (
        <Alert variant="danger">
          {intl.formatMessage(messages.submitError)}
        </Alert>
      )}

      {questions.map(question => (
        <div key={question.id} className="survey-question">
          <label
            id={`survey-question-${question.id}`}
            className="survey-question-label"
            htmlFor={`survey-answer-${question.id}`}
          >
            {question.question}
            {question.required && <span className="text-danger ml-1">*</span>}
          </label>
          {renderQuestionInput(question)}
        </div>
      ))}

      <div className="d-flex flex-wrap align-items-center">
        <Button
          type="submit"
          variant="primary"
          className="mr-2 mb-2"
          disabled={submitting}
        >
          {intl.formatMessage(submitting ? messages.submitting : messages.submit)}
        </Button>
        <Button
          type="button"
          variant="outline-primary"
          className="mb-2"
          onClick={onCancel}
          disabled={submitting}
        >
          {intl.formatMessage(messages.cancel)}
        </Button>
      </div>
    </form>
  );
};

SurveyForm.propTypes = {
  courseSurvey: PropTypes.shape({
    id: PropTypes.number.isRequired,
    survey: PropTypes.number.isRequired,
    survey_name: PropTypes.string,
  }).isRequired,
  intl: intlShape.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func.isRequired,
};

export default injectIntl(SurveyForm);
